import { estimateMutationScopeId } from '@/api/mutations/estimates/hydrateEstimateCaches';
import projectKeys from '@/api/queries/project/keyFactory';
import { GetProjectEstimatesQuery } from '@/gql/graphql';
import {
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

type EstimateMutationContext = {
  previous: GetProjectEstimatesQuery | undefined;
};

type BaseMutationArgs<TData, TVariables> = {
  projectId: string;
  estimateId: string;
  options?: UseMutationOptions<TData, Error, TVariables>;
};

type BaseMutationHook<TData, TVariables> = (
  args: BaseMutationArgs<TData, TVariables>
) => UseMutationResult<TData, Error, TVariables>;

/**
 * Wraps a plain estimate mutation hook with optimistic-update behaviour against
 * the GetProjectEstimatesQuery cache: cancel-on-mutate, snapshot, in-place apply,
 * and onError rollback. The base hook still owns the network call and cache
 * hydration in `onSuccess`; the factory only layers the optimistic write.
 *
 * Mirrors `pipelineConfigPage/api/mutations/createDraftPipelineMutation` but for
 * the estimates cache, and as a wrapper over a base hook rather than its own
 * useMutation.
 */
export type OptimisticEstimateContext = {
  projectId: string;
  estimateId: string;
};

export function createOptimisticEstimateMutation<TData, TVariables>(config: {
  useBaseMutation: BaseMutationHook<TData, TVariables>;
  applyOptimisticUpdate: (
    data: GetProjectEstimatesQuery,
    variables: TVariables,
    context: OptimisticEstimateContext
  ) => GetProjectEstimatesQuery;
}) {
  return (args: {
    projectId: string;
    estimateId: string;
    options?: UseMutationOptions<TData, Error, TVariables>;
  }) => {
    const { projectId, estimateId } = args;
    const queryClient = useQueryClient();
    const { onMutate, onError, ...restOptions } = args.options ?? {};

    return config.useBaseMutation({
      projectId,
      estimateId,
      options: {
        ...restOptions,
        // Shared scope across every estimate mutation, keyed per-estimate so
        // edits to different estimates run in parallel while same-estimate
        // mutations serialise. Without this, two in-flight mutations could
        // each snapshot `previous` independently and a late `onError`
        // rollback would restore a snapshot that doesn't include the other's
        // successful write.
        scope: { id: estimateMutationScopeId(estimateId) },
        onMutate: async (variables) => {
          const queryKey = projectKeys.estimates(projectId);
          // Awaited so an in-flight refetch can't land after our optimistic
          // write and clobber it with stale server data.
          await queryClient.cancelQueries({ queryKey });

          const previous =
            queryClient.getQueryData<GetProjectEstimatesQuery>(queryKey);

          queryClient.setQueryData<GetProjectEstimatesQuery>(queryKey, (old) =>
            old
              ? config.applyOptimisticUpdate(old, variables, {
                  projectId,
                  estimateId,
                })
              : old
          );

          await onMutate?.(variables);

          const context: EstimateMutationContext = { previous };
          return context;
        },
        onError: (err, variables, context) => {
          const previous = (context as EstimateMutationContext | undefined)
            ?.previous;
          if (previous) {
            queryClient.setQueryData(
              projectKeys.estimates(projectId),
              previous
            );
          }
          onError?.(err, variables, context);
        },
      },
    });
  };
}
